import { useMemo } from 'react';
import { ILateFinalXm } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ReportWrapper } from './styles';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: ILateFinalXm[]
}

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();

        e.columnApi.applyColumnState({
            state: [
                {
                    colId: 'requestedDate',
                    sort: 'desc'
                }
            ]
        });
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    let columns: ColDef[] = [
        {
            colId: 'xmTrackingId',
            field: 'xmTrackingId',
            headerName: 'Order #',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Order #</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ILateFinalXm;

                return <>{data.xmTrackingId != null ? data.xmTrackingId : '--'}</>
            },
            headerClass: 'list-header',
            cellClass: 'list-cell list-cell-order',
            width: 75,
            hide: false
        },
        {
            colId: 'swapNumber',
            headerName: 'Swap',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 75,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ILateFinalXm;

                if (data.chainId != null && data.clusterNumber != null && data.positionNumber != null)
                    return <>{`${data.chainId}/${data.clusterNumber}/${data.positionNumber}`}</>;
                else
                    return <></>;
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ILateFinalXm;

                if (data.chainId != null && data.clusterNumber != null && data.positionNumber != null) {
                    return parseInt(`${data.chainId}/${data.clusterNumber}/${data.positionNumber}`);
                }

                return 0;
            },
            sortable: true
        },
        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'Recipient Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Center</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 130,
            sortable: true
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Alias</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 120,
            sortable: true
        },
        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 130,
            sortable: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ILateFinalXm;

                if (data.rdnCenterId > 0) {
                    return <>{data.donorCenter} (<span style={{ color: 'red' }}>{data.rdnCenter}</span>)</>;
                }

                return <>{data.donorCenter}</>;
            }
        },
        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor Alias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Alias</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 120,
            sortable: true
        },
        {
            colId: 'status',
            field: 'status',
            headerName: 'Status',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ILateFinalXm;

                return <>{data.status ? data.status : '--'}</>
            },
            headerClass: 'list-header',
            cellClass: 'list-cell',
            sortable: true
        },
        {
            colId: 'requestedDate',
            field: 'requestedDate',
            headerName: 'Date Requested',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date<br />Requested</SortableHeaderComponent>,
            cellRendererFramework: (params:ICellRendererParams) => {
                let data = params.data as ILateFinalXm;

                return <>{data.requestedDate ? data.requestedDate : '--'}</>
            },
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 105,
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'deadlineDate',
            field: 'deadlineDate',
            headerName: 'Deadline',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Deadline</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 135,
            suppressSizeToFit: true,
            editable: true,
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'uploadDateTime',
            field: 'uploadDateTime',
            headerName: 'Upload Date/Time',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Upload<br />Date/Time</SortableHeaderComponent>,
            cellRendererFramework: (params:ICellRendererParams) => {
                let data = params.data as ILateFinalXm;

                return <>{data.uploadDateTime ? data.uploadDateTime : '--'}</>
            },
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 145,
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'transplantDate',
            field: 'transplantDate',
            headerName: 'Transplant Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Transplant<br />Date</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 105,
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'daysLate',
            field: 'daysLate',
            headerName: 'Days Late',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Days<br />Late</SortableHeaderComponent>,
            cellRendererFramework: (params:ICellRendererParams) => {
                let data = params.data as ILateFinalXm;

                return <>{data.daysLate != null ? data.daysLate : '--'}</>
            },
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 85,
            sortable: true
        }
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={45}
                groupHeaderHeight={30}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No donor record review &amp; VXM records recorded late...</div>}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
            />
        </ReportWrapper>
    );
}

export default Grid;